import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Check, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, streamSSE } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";

const CHANNELS = ["Instagram-innlegg", "Facebook-innlegg", "Google Ads-annonse", "SEO-tekst for nettsiden", "Nyhetsbrev / e-post", "Kampanjeplan (høst 2026)", "TikTok / Reels-manus"];
const IDEAS = ["Bunadsjustering før 17. mai – bestill i god tid", "Høstkampanje: gjør høstjakken klar – opplegg av ermer og nytt fôr", "Brudekjoler 2027: to veier til drømmekjolen", "Nytt sykurs for nybegynnere i Bergen", "Julebord-sesong: tilpasning av dress og festkjole"];

const DraftItem = ({ d, onChange }) => {
  const approve = async () => { await http.patch(`/admin/marketing/drafts/${d.id}`, { status: d.status === "godkjent" ? "utkast" : "godkjent" }); onChange(); };
  const remove = async () => { await http.delete(`/admin/marketing/drafts/${d.id}`); onChange(); };
  return (
    <div className="border border-[#E5E0D8] p-5" data-testid={`draft-${d.id}`}>
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs uppercase tracking-[0.15em] text-[#8C6E48]">{d.channel}</p>
        <span className={`text-xs px-2 py-1 ${d.status === "godkjent" ? "bg-[#1A1A1A] text-white" : "bg-[#F0EBE1]"}`}>{d.status === "godkjent" ? "Godkjent" : "Utkast"}</span>
      </div>
      <p className="text-sm whitespace-pre-wrap line-clamp-6">{d.content}</p>
      <div className="flex gap-3 mt-4 text-xs">
        <button onClick={() => { navigator.clipboard.writeText(d.content); toast.success("Kopiert"); }} className="flex items-center gap-1 hover:text-[#8C6E48]" data-testid={`draft-copy-${d.id}`}><Copy className="w-3.5 h-3.5" />Kopier</button>
        <button onClick={approve} className="flex items-center gap-1 hover:text-[#8C6E48]" data-testid={`draft-approve-${d.id}`}><Check className="w-3.5 h-3.5" />{d.status === "godkjent" ? "Tilbake til utkast" : "Godkjenn"}</button>
        <button onClick={remove} className="flex items-center gap-1 hover:text-red-700 ml-auto" data-testid={`draft-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" />Slett</button>
      </div>
    </div>
  );
};

const SEASON_POSTS = [
  ["17. mai og bunad", "Instagram-innlegg", "Bunadsjustering før 17. mai og konfirmasjon – oppfordre til å bestille i god tid. Gratis bunadskonsultasjon."],
  ["Halloween", "Instagram-innlegg", "Halloween: tilpasning, reparasjon og omsøm av kostymer, eller lær å sy ditt eget på privatkurs."],
  ["Julebord og firmafest", "Facebook-innlegg", "Julebord og firmafest: få dressen og festkjolen tilpasset før høysesongen i november."],
  ["Jul og nyttår", "Instagram-innlegg", "Jul og nyttår: festantrekk, dress, kjole og bunad tilpasset og klar til høytiden. Bestill tid i god tid."],
];

export default function Marketing() {
  const [channel, setChannel] = useState(CHANNELS[0]);
  const [tone, setTone] = useState("elegant og personlig");
  const [topic, setTopic] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const load = () => http.get("/admin/marketing/drafts").then((r) => setDrafts(r.data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!topic.trim()) return toast.error("Skriv et tema eller velg en idé");
    setBusy(true);
    setOut("");
    try { await streamSSE("/admin/marketing/stream", { channel, tone, topic }, setOut); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const seasonPost = async ([, ch, t]) => {
    setChannel(ch);
    setTopic(t);
    setBusy(true);
    setOut("");
    try { await streamSSE("/admin/marketing/stream", { channel: ch, tone, topic: t }, setOut); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const [seoBusy, setSeoBusy] = useState(false);
  const runSeo = async () => {
    setSeoBusy(true);
    try { await http.post("/admin/seo/analyze"); toast.success("SEO-rapport lagret under utkast"); load(); } catch (e) { toast.error("Kunne ikke lage rapport"); }
    setSeoBusy(false);
  };
  const pingIndex = async () => {
    try { const { data } = await http.post("/admin/seo/indexnow"); toast.success(`IndexNow svarte ${data.status}. ${data.note}`); } catch (e) { toast.error("IndexNow feilet"); }
  };
  const save = async () => {
    await http.post("/admin/marketing/drafts", { channel, topic, content: out });
    toast.success("Lagret som utkast");
    load();
  };

  return (
    <div data-testid="admin-marketing">
      <AdminTitle title="AI Markedsføring" sub="Lag utkast til innlegg, annonser og kampanjer. Ingenting publiseres automatisk – du godkjenner og kopierer selv. Agenten finner ikke på priser eller tilbud." />
      <div className="grid xl:grid-cols-12 gap-10">
        <div className="xl:col-span-7 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="rounded-none" data-testid="mkt-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Tone" className="rounded-none" data-testid="mkt-tone-input" />
          </div>
          <Textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Hva skal innholdet handle om?" className="rounded-none" data-testid="mkt-topic-input" />
          <div className="p-4 border border-[#1A1A1A]" data-testid="mkt-seo-agent">
            <p className="text-xs uppercase tracking-[0.15em] text-[#8C6E48] mb-1">SEO-, GEO- og AEO-agent</p>
            <p className="text-xs text-[#5C5A56] mb-3">Kjører automatisk hver mandag kl. 07 (UTC) og lager en rapport basert på faktiske henvendelser og kundespørsmål. Du får e-post når den er klar.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={runSeo} disabled={seoBusy} className="text-xs px-3 py-2 bg-[#1A1A1A] text-white disabled:opacity-50" data-testid="mkt-seo-run-btn">{seoBusy ? "Analyserer…" : "Kjør SEO-analyse nå"}</button>
              <button onClick={pingIndex} className="text-xs px-3 py-2 border border-[#1A1A1A]" data-testid="mkt-indexnow-btn">Varsle søkemotorer (IndexNow)</button>
            </div>
          </div>
          <div className="p-4 bg-[#F0EBE1]" data-testid="mkt-season-posts">
            <p className="text-xs uppercase tracking-[0.15em] text-[#8C6E48] mb-2">Sesonginnlegg med ett klikk</p>
            <div className="flex flex-wrap gap-2">
              {SEASON_POSTS.map((p, n) => <button key={p[0]} onClick={() => seasonPost(p)} disabled={busy} className="text-xs px-3 py-2 bg-[#1A1A1A] text-white hover:bg-[#8C6E48] transition-colors disabled:opacity-50" data-testid={`mkt-season-${n}`}>{p[0]}</button>)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {IDEAS.map((i, n) => <button key={i} onClick={() => setTopic(i)} className="text-xs px-3 py-1.5 border border-[#C4A47C] text-[#8C6E48] hover:bg-[#C4A47C] hover:text-white transition-colors" data-testid={`mkt-idea-${n}`}>{i}</button>)}
          </div>
          <button onClick={generate} disabled={busy} className="flex items-center gap-2 px-6 py-3 bg-[#1A1A1A] text-white text-sm hover:bg-[#8C6E48] transition-colors disabled:opacity-50" data-testid="mkt-generate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />} Generer utkast
          </button>
          <Textarea rows={16} value={out} onChange={(e) => setOut(e.target.value)} placeholder="Utkastet vises her – du kan redigere fritt." className="rounded-none bg-white" data-testid="mkt-output" />
          {out && !busy && <button onClick={save} className="px-6 py-3 border border-[#1A1A1A] text-sm hover:bg-[#1A1A1A] hover:text-white transition-colors" data-testid="mkt-save-btn">Lagre som utkast</button>}
        </div>
        <div className="xl:col-span-5">
          <h2 className="text-2xl font-medium mb-4">Lagrede utkast</h2>
          <div className="space-y-4" data-testid="mkt-drafts">
            {drafts.length === 0 && <p className="text-sm text-[#5C5A56]">Ingen utkast ennå.</p>}
            {drafts.map((d) => <DraftItem key={d.id} d={d} onChange={load} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
